import { hexToHSB, hsvToHSL, hsvToRGB, hsvToHex } from "./color";

interface IHsv
{
    hue?: number,
    saturation?: number,
    value?: number
}

interface IColorDictionary
{
    name?: string,
    hueRange?: number[],
    lowerBounds?: number[][],
    saturationRange?: number[],
    brightnessRange?: number[]
}

export interface IRandomColorOptions
{
    hue?: string,
    count?: number,
    seed?: string,
    luminosity?: | "lighter" | "light" | "bright" | "dark" | "random",
    format?: "rgb" | "hsl" | "hex"
}

export default class RandomColor
{
    private _colorDictionary: IColorDictionary[] = [];
    private _colors: string[];
    private _seed: number;

    constructor()
    {
        this.populateColorDictionary();
    }

    public generate(options: IRandomColorOptions = {}): string[]
    {
        this._seed = this.getSeed(options.seed);

        // Check if we need to generate multiple colors
        if (options.count != null)
        {
            var totalColors = options.count
            var colors = [];
            options.count = null;

            while (totalColors > colors.length)
            {
                // Since we're generating multiple colors,
                // incremement the seed. Otherwise we'd just
                // generate the same color each time...
                if (this._seed && options.seed)
                    options.seed += 1000;

                colors.push(this.generate(options)[0]);
            }

            options.count = totalColors;
            return colors;
        }

        var hsv: IHsv = {};
        hsv.hue = this.getHue(options.hue);
        hsv.saturation = this.getSaturation(hsv.hue, options);
        hsv.value = this.getBrightness(hsv.hue, hsv.saturation, options);

        switch (options.format)
        {
            case "hsl":
                {
                    var hsl = hsvToHSL([hsv.hue, hsv.saturation, hsv.value]);
                    return ["hsl(" + hsl[0] + ", " + hsl[1] + "%, " + hsl[2] + "%)"];
                }
            case "rgb":
                {
                    var rgb = hsvToRGB([hsv.hue, hsv.saturation, hsv.value]);
                    return ["rgb(" + rgb.join(", ") + ")"];
                }
            default:
                return [hsvToHex([hsv.hue, hsv.saturation, hsv.value])];
        }
    }

    private populateColorDictionary()
    {
        this._colorDictionary.push({
            name: "monochrome",
            hueRange: null,
            lowerBounds: [[0, 0], [100, 0]],
            saturationRange: [0, 100],
            brightnessRange: [0, 0]
        });

        this._colorDictionary.push({
            name: "red",
            hueRange: [-26, 18],
            lowerBounds: [[20, 100], [30, 92], [40, 89], [50, 85], [60, 78], [70, 70], [80, 60], [90, 55], [100, 50]],
            saturationRange: [20, 100],
            brightnessRange: [50, 100]
        });

        this._colorDictionary.push({
            name: "orange",
            hueRange: [19, 46],
            lowerBounds: [[20, 100], [30, 93], [40, 88], [50, 86], [60, 85], [70, 70], [100, 70]],
            saturationRange: [20, 100],
            brightnessRange: [70, 100]
        });

        this._colorDictionary.push({
            name: "yellow",
            hueRange: [47, 62],
            lowerBounds: [[25, 100], [40, 94], [50, 89], [60, 86], [70, 84], [80, 82], [90, 80], [100, 75]],
            saturationRange: [25, 100],
            brightnessRange: [75, 100]
        });

        this._colorDictionary.push({
            name: "green",
            hueRange: [63, 178],
            lowerBounds: [[30, 100], [40, 90], [50, 85], [60, 81], [70, 74], [80, 64], [90, 50], [100, 40]],
            saturationRange: [30, 100],
            brightnessRange: [40, 100]
        });

        this._colorDictionary.push({
            name: "blue",
            hueRange: [179, 257],
            lowerBounds: [[20, 100], [30, 86], [40, 80], [50, 74], [60, 60], [70, 52], [80, 44], [90, 39], [100, 35]],
            saturationRange: [20, 100],
            brightnessRange: [35, 100]
        });

        this._colorDictionary.push({
            name: "purple",
            hueRange: [258, 282],
            lowerBounds: [[20, 100], [30, 87], [40, 79], [50, 70], [60, 65], [70, 59], [80, 52], [90, 45], [100, 42]],
            saturationRange: [20, 100],
            brightnessRange: [42, 100]
        });

        this._colorDictionary.push({
            name: "pink",
            hueRange: [283, 334],
            lowerBounds: [[20, 100], [30, 90], [40, 86], [60, 84], [80, 80], [90, 75], [100, 73]],
            saturationRange: [20, 100],
            brightnessRange: [73, 100]
        });
    }

    private getHue(hue: string): number
    {
        var hueRange: number[];

        if (!isNaN(parseInt(hue)))
        {
            var number = parseInt(hue);
            if (number < 360 && number > 0)
            {
                hueRange = [number, number];
            }
        }
        else if (typeof hue === "string")
        {
            let color: IColorDictionary = this._colorDictionary.where(c => c.name === hue.toLowerCase().trim()).first();
            if (color != null && color.hueRange != null)
            {
                hueRange = color.hueRange;
            }
            else if (hue.match(/^#?([0-9A-F]{3}|[0-9A-F]{6})$/i))
            {
                var converted = hexToHSB(hue)[0];
                hueRange = [converted, converted];
            }
            else
            {
                hueRange = [0, 360];
            }
        }
        else
        {
            hueRange = [0, 360];
        }

        return this.randomize(hueRange[0], hueRange[1]);
    }

    private getSaturation(hue: number, options: IRandomColorOptions): number
    {
        if (options.hue === "monochrome")
        {
            return 0;
        }

        if (options.luminosity === "random")
        {
            return this.randomize(0, 100);
        }

        var color: IColorDictionary = this.getColor(hue);
        var min: number = color.saturationRange[0];
        var max: number = color.saturationRange[1];

        switch (options.luminosity)
        {
            case "lighter":
                max = 55;
                break;
            case "light":
                min = 40;
                max = 60;
                break;
            case "bright":
                min = 55;
                break;
            case "dark":
                min = max - 10;
                break;
        }

        return this.randomize(min, max);
    }

    private getBrightness(hue: number, saturation: number, options: IRandomColorOptions): number
    {
        var min = this.getMinimumBrightness(hue, saturation)
        var max = 100;

        switch (options.luminosity)
        {
            case "dark":
                max = min + 20;
                break;
            case "light":
                min = (max + min) / 2;
                break;
            case "random":
                min = 0;
                max = 100;
                break;
        }

        return this.randomize(min, max);
    }

    private getMinimumBrightness(hue: number, saturation: number)
    {
        var color: IColorDictionary = this.getColor(hue);
        var { lowerBounds } = color;

        for (var i = 0; i < lowerBounds.length - 1; i++)
        {
            let s1 = lowerBounds[i][0];
            let v1 = lowerBounds[i][1];
            let s2 = lowerBounds[i + 1][0];
            let v2 = lowerBounds[i + 1][1];

            if (saturation >= s1 && saturation <= s2)
            {
                let m = (v2 - v1) / (s2 - s1);
                let b = v1 - m * s1;
                return m * saturation + b;
            }
        }

        return 0;
    }

    private getSeed(seed: string | number): number
    {
        if (seed != null && seed === parseInt(seed as string, 10))
        {
            return seed;
        }
        else if (typeof seed === 'string')
        {
            return this.stringToInteger(seed);
        }
        else if (seed != null)
        {
            throw new TypeError("The seed value must be an integer or string");
        }

        return null;
    }

    /**
     * Ermittelt die Farbe anhand des Hue Werts.
     * @param hue
     */
    private getColor(hue: number)
    {
        // Maps red colors to make picking hue easier
        if (hue >= 334 && hue <= 360)
        {
            hue -= 360;
        }

        for (var color of this._colorDictionary)
        {
            if (color.hueRange && hue >= color.hueRange[0] && hue <= color.hueRange[1])
            {
                return color;
            }
        }

        throw new Error("Color not found");
    }

    /**
     * Gibt eine Zufallszahl anhand der übergebenen min und max Werte zurück.
     * @param min
     * @param max
     */
    private randomize(min: number, max: number): number
    {
        if (this._seed == null)
        {
            return Math.floor(min + Math.random() * (max + 1 - min));
        }
        else
        {
            // Seeded random algorithm from http://indiegamr.com/generate-repeatable-random-numbers-in-js/
            var newMax = max || 1;
            var newMin = min || 0;

            this._seed = (this._seed * 9301 + 49297) % 233280;
            var rnd = this._seed / 233280.0;

            return Math.floor(newMin + rnd * (newMax - newMin));
        }
    }

    /**
     * Converts a string into a number.
     * @param str
     */
    private stringToInteger(str: string): number
    {
        var total = 0
        for (var i = 0; i !== str.length; i++)
        {
            if (total >= Number.MAX_SAFE_INTEGER) break;
            total += str.charCodeAt(i)
        }
        return total
    }
}

